<template>
  <div class="a11y-modal">
    <div class="title">
      <h2>Add Adjustment</h2>
      <button class="close" @click="closeModal">×</button>
    </div>

    <div class="content">
      <form id="AddPayrollAdjustmentForm" novalidate @submit.prevent="submit">
        <form-component>
          <div class="row">
          <div class="col-12 mb-4">
            <input-type-ahead
              :list="counselors"
              :value="counselorId"
              :searchchange="getCounselors"
              :selected-value="selectedCounselor"
              placeholder="Search for a Counselor..."
              label="Counselor"
              option-value="id"
              option-text="display_label"
              required
          />
          <p v-if="!loading && (!counselors || counselors.length === 0)">
            No counselors found
          </p>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6 mb-4">
              <input-text
                id="adjustmentAmount"
                v-model="adjustmentAmount"
                v-validate="'required|decimal|excluded:0'"
                data-vv-as="Adjustment Amount"
                data-vv-name="adjustmentAmount"
                :error="errors.first('adjustmentAmount')"
                name="adjustmentAmount"
                label="Adjustment Amount"
                type="number"
                placeholder="$0.00"
                required
              />
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6 mb-4">
            <input-select
              id="isImmediatePayout"
              v-model="isImmediatePayout"
              name="isImmediatePayout"
              label="Payout Immediately"
              type="checkbox"
              :options="[
                { id: 'true', display_label: 'Yes' },
                { id: 'false', display_label: 'No' }
              ]"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-12 mb-4">
            <input-text-area id="explanation" 
                            v-model="explanation" 
                            v-validate="'required|min:10'"
                            data-vv-as="explanation"
                            data-vv-name="explanation"
                            :error="errors.first('explanation')" 
                            name="explanation" 
                            label="Explanation" 
                            type="text" 
                            maxlength="1000" 
                            required
                          />
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6 offset-md-6 text-right">
            <button type="button" class="btn m-1" @click="closeModal">Cancel</button>
            <button class="btn btn-primary m-1" 
                    type="submit" 
                    form="AddPayrollAdjustmentForm" 
                    :disabled="submitting"
                    :aria-disabled="submitting">
              <font-awesome-icon v-if="submitting" :spin="true" :icon="['fas', 'spinner']" class="icon alt" />
              <span v-else>Add Adjustment</span>
            </button>
          </div>
        </div>

      </form-component>
      </form>
    </div>


  </div>
</template>

<script>

import PayrollClient from '@/api/CounselorManagement/PayrollClient';
import AdminClient from '@/api/AdminClient';
import FormComponent from '@/components/FormComponent.vue';
import InputText from '@/components/inputs/InputText.vue';
import InputTextArea from '@/components/inputs/InputTextArea.vue';
import ServerValidator from '@/helpers/mixins/server-validator';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputTypeAhead from '@/components/inputs/InputTypeAhead.vue';

export default {
  name: 'AddPayrollAdjustmentModal',

  components: {
    FormComponent,
    InputText,
    InputTextArea,
    InputSelect,
    InputTypeAhead
  },

   mixins: [ServerValidator],

  data() {
    return {
      payrollClient: new PayrollClient(this.$http),
      adminClient: new AdminClient(this.$http),
      submitting: false,
      adjustmentAmount: '',
      explanation: '',
      counselorId: 0,
      isImmediatePayout: 'false',
      counselors: [],
      loading: true,
      _getCounselorsTimeout: null
    };
  },

  mounted() {

  },

  methods: {
    closeModal() {
      this.$modal.close();
      this.adjustmentAmount = '0',
      this.explanation ='',
      this.counselorId = '',
      this.isImmediatePayout = 'false'
    },

    async submit() {
      this.$serverValidator.clear();

      const valid = await this.$validator.validateAll();
      if (!valid) {
        return;
      }

      this.submitting = true;
      try {
        await this.payrollClient.adjust(
          this.counselorId,
          this.adjustmentAmount,
          this.explanation,
          this.isImmediatePayout === 'true'
        );
        this.$store.commit('setGlobalAlert', {
          msg: 'Adjustment was successfully added!',

        });
      } catch (e) {
        if (e.status === 400 && e.body.errors) {
          const errors = e.body.errors;
          let message = 'Received the following errors:';
          for(const key in errors) {
            if(!errors[key].length)
              continue;
            message += ` ${key} ${errors[key][0].toLowerCase()}`
            for(let i = 1; i < errors[key].length; i++)
              message += `, ${key} ${errors[key][i].toLowerCase()}`
            if(message[message.length-1] != '.')
              message += '.';
          }

          this.$store.commit('setGlobalAlert', { msg: message, error: true });

        } else {
          console.error(e);
          this.$store.commit('setGlobalAlert', {
            msg: 'There was an error adding this adjustment. Please try again!',
            error: true
          });
        }
      } finally {
        this.submitting = false;
        this.closeModal();
      }
    }, 
    getCounselors(query, done) {
      
      if (this.counselor && !query) {
        // we just selected one, do not empty the results
        return;
      }

      if (this._getCounselorsTimeout) {
        clearTimeout(this._getCounselorsTimeout);
        this._getCounselorsTimeout = null;
      }

      this._getCounselorsTimeout = setTimeout(() => {
        if (this.counselorId) {
          return;
        }

        const searchCriteria = [];
        for(const split of query.split(/\s/)) {
          if(split)
           searchCriteria.push(split);
        }

        this.adminClient.getAllCounselorsByLookup(searchCriteria).then((response) => {
          if (!this.counselorId) {
            // if we just picked one as an admin, stop refreshing the counselor list.
            this.counselor = null;
          }

          this.counselors = response.counselors.map(x => ({display_label: x.firstName + ' ' + x.lastName + ' (' + x.email + ')', id: x.id})) || [];
          this.loading = false;
        }).catch(() => {
          if (!this.counselorId) {
            // if we just picked one as an admin, stop refreshing the counselor list.
            this.counselor = null;
          }

          this.counselors = [];
          this.loading = false;
        });
      }, 300);
    },
    selectedCounselor(v) {
      this.counselorId = v;
    }
  }
};
</script>

<style lang="scss" scoped>
.a11y-modal {

  background-color: $dark-blue;
  h2 {
    color:#fff;
  }
  ::v-deep .form .form__input {
    label {
      color: #fff;
      text-transform: none;
      font-size: 15px;
      letter-spacing: 0;
      margin-bottom: 0.5rem;
      font-weight: 500;
    }
    textarea, input {
      border: solid 1px #fff;
      //background: #0f2a45 ;
      //color: inherit;

      &:focus {
        color: inherit;
        border: solid 1px $dark-blue;
        background: #f8f8f8;
      }

      &::placeholder {
        color: inherit;
      }
    }
  }
  .close {
    color:#f8f8f8;
  }
}
</style>
